import {
  GroupStudentWorksheet,
  GroupStudentWorksheetColl,
  GroupStudentWorksheetProblem,
} from 'src/dto'
import type { SchoolGrade } from 'src/dto/Student/SchoolGrade'
import CheckableMap from 'src/models/CheckableMap'

import studentWorksheetApi from '@/api/studentWorksheet.api'
import type { Student, Worksheet } from '@/dto'
import {
  StudentWorksheet,
  StudentWorksheetColl,
  StudentWorksheetProblem,
  StudentWorksheetProblemColl,
} from '@/dto'
import type { CustomRequestConfigMeta } from '@/utils/axios/maxios'
import { toastError } from '@/utils/toast'

import { makeAdapter } from './Adapter'
import type Controller from './Controller'

/***
 * @see StudentWorksheet
 ***/
class LessonWorksheetController implements Controller {
  adapter = {
    studentWorksheet: makeAdapter({
      from: (data: res.StudentWorksheet) => {
        return new StudentWorksheet({
          ...data,
          assignDatetime: new Date(data.assignDatetime),
          ...(data.scoreDatetime && {
            scoreDatetime: new Date(data.scoreDatetime),
          }),
          ...(data.openDatetime && {
            openDatetime: new Date(data.openDatetime),
          }),
        })
      },
    }),
    groupStudentWorksheet: makeAdapter({
      from: (data: res.GroupStudentWorksheet) => {
        return new GroupStudentWorksheet({
          ...data,
          id: data.worksheet.id,
          assignDatetime: new Date(data.assignDatetime),
        })
      },
    }),
    studentWorksheetProblem: makeAdapter({
      from: (data: res.StudentWorksheetProblem) => {
        return new StudentWorksheetProblem({
          id: data.worksheetProblemId,
          result: data.result,
          userAnswer: data.userAnswer,
          problemId: data.problem.id,
          index: data.problem.index,
          favorite: data.problem.favorite,
          type: data.problem.type,
          answer: data.problem.answer,
          problemImageUrl: data.problem.problemImageUrl,
          answerImageUrl: data.problem.answerImageUrl,
          solutionImageUrl: data.problem.solutionImageUrl,
          handwrittenNoteUrl: data.handwrittenNoteUrl,
          videoMatched: data.problem.videoMatched,
          video: data.problem.video,
          conceptName: data.problem.conceptName,
          tagTop: data.problem.tagTop,
          level: data.problem.level,
          answerUnits: data.problem.answerUnits,
          conceptId: data.problem.conceptId,
          sourceType: data.problem.sourceType,
        })
      },
    }),
    groupStudentWorksheetProblem: makeAdapter({
      from: (data: res.GroupStudentWorksheetProblem) => {
        return new GroupStudentWorksheetProblem(data)
      },
    }),
  }

  fetchStudentWorksheets = async (studentId: Student['id'], queryString) => {
    const {
      data: {
        data: { content, last },
        code,
        message,
      },
    } = await studentWorksheetApi.getStudentWorksheets(studentId, queryString)

    const parseData = content.map(this.adapter.studentWorksheet.from)
    const studentWorksheetMap = CheckableMap.fromArr(parseData, 'id')

    if (code === 'FIND_DELETED_WORKSHEET') {
      toastError(message || '')
    }

    return {
      studentWorksheetColl: new StudentWorksheetColl(studentWorksheetMap),
      isLast: last,
    }
  }
  fetchSpecificStudentWorksheet = async (studentWorksheetId: StudentWorksheet['id']) => {
    const {
      data: { data },
    } = await studentWorksheetApi.getStudentWorksheet(studentWorksheetId)

    const studentWorksheet = this.adapter.studentWorksheet.from(data)

    return studentWorksheet
  }

  fetchGradeStudentWorksheets = async (
    schoolType: SchoolGrade['schoolType'],
    grade: SchoolGrade['grade'],
    queryString: string,
  ) => {
    const {
      data: {
        data: { content, last, empty },
        code,
        message,
      },
    } = await studentWorksheetApi.getGradeStudentWorksheets(schoolType, grade, queryString)

    const parseData = content.map(this.adapter.groupStudentWorksheet.from)
    const gradeStudentWorksheetMap = CheckableMap.fromArr(parseData, 'id')

    if (code === 'FIND_DELETED_WORKSHEET') {
      toastError(message || '')
    }

    return {
      gradeStudentWorksheetColl: new GroupStudentWorksheetColl(gradeStudentWorksheetMap),
      isLast: last,
      empty,
    }
  }

  fetchClassStudentWorksheets = async (lessonClassId: string, queryString: string) => {
    const {
      data: {
        data: { content, last, empty },
        code,
        message,
      },
    } = await studentWorksheetApi.getClassStudentWorksheets(lessonClassId, queryString)

    const parseData = content.map(this.adapter.groupStudentWorksheet.from)
    const classStudentWorksheetMap = CheckableMap.fromArr(parseData, 'id')

    if (code === 'FIND_DELETED_WORKSHEET') {
      toastError(message || '')
    }

    return {
      classStudentWorksheetColl: new GroupStudentWorksheetColl(classStudentWorksheetMap),
      isLast: last,
      empty,
    }
  }

  fetchStudentWorksheetProblems = async (
    studentWorksheetId: StudentWorksheet['id'],
    queryString: string,
    meta?: { meta: CustomRequestConfigMeta },
  ) => {
    const {
      data: {
        data: { content, pageable },
      },
    } = await studentWorksheetApi.getStudentWorksheetProblems(studentWorksheetId, queryString, meta)

    const parseData = content.map(this.adapter.studentWorksheetProblem.from)
    const studentWorksheetProblemMap = CheckableMap.fromArr(parseData, 'id')

    return {
      studentWorksheetProblemColl: new StudentWorksheetProblemColl(studentWorksheetProblemMap),
      pageable,
    }
  }

  fetchGradeStudentWorksheetProblems = async (
    schoolType: SchoolGrade['schoolType'],
    grade: SchoolGrade['grade'],
    worksheetId: StudentWorksheet['worksheet']['id'],
    queryString: string,
    meta?: { meta: CustomRequestConfigMeta },
  ) => {
    const {
      data: {
        data: { content, pageable },
      },
    } = await studentWorksheetApi.getGradeStudentWorksheetProblems(
      schoolType,
      grade,
      worksheetId,
      queryString,
      meta,
    )

    const parseData = content.map(this.adapter.groupStudentWorksheetProblem.from)

    return {
      gradeStudentWorksheetProblems: this.sortScoringStudents(parseData),
      pageable,
    }
  }

  fetchClassStudentWorksheetProblems = async (
    lessonClassId: string,
    worksheetId: StudentWorksheet['worksheet']['id'],
    queryString: string,
    meta?: { meta: CustomRequestConfigMeta },
  ) => {
    const {
      data: {
        data: { content, pageable },
      },
    } = await studentWorksheetApi.getClassStudentWorksheetProblems(
      lessonClassId,
      worksheetId,
      queryString,
      meta,
    )

    const parseData = content.map(this.adapter.groupStudentWorksheetProblem.from)

    return {
      classStudentWorksheetProblems: this.sortScoringStudents(parseData),
      pageable,
    }
  }

  assignStudentWorksheet = async (
    studentIdList: Student['id'][],
    worksheetIdList: Worksheet['id'][],
  ) => {
    const res = await studentWorksheetApi.postStudentWorksheets(studentIdList, worksheetIdList)

    return res
  }

  patchStudentWorksheet = async (
    studentWorksheetId: StudentWorksheet['id'],
    dateTime: { [key in 'assignDatetime' | 'scoreDatetime']?: string },
  ) => {
    await studentWorksheetApi.patchStudentWorksheet(studentWorksheetId, dateTime)
  }

  patchStudentWorksheetScoring = async (
    studentWorksheetId: StudentWorksheet['id'],
    data: req.ScoringResult[],
  ) => {
    const {
      data: { data: scoringResult },
    } = await studentWorksheetApi.patchStudentWorksheetScoring(studentWorksheetId, data)
    return scoringResult
  }

  deleteStudentWorksheets = async (studentWorksheetIdList: StudentWorksheet['id'][]) =>
    await studentWorksheetApi.deleteStudentWorksheets(studentWorksheetIdList)

  private sortScoringStudents = (studentWorksheetProblems: GroupStudentWorksheetProblem[]) =>
    studentWorksheetProblems.sort((a, b) =>
      a.studentName > b.studentName ? 1 : a.studentName < b.studentName ? -1 : 0,
    )

  fetchVirtualScoringStudentWorksheets = async (
    studentWorksheetIdList: StudentWorksheet['id'][],
  ) => {
    const {
      data: { data },
    } = await studentWorksheetApi.getStudentWorksheetVirtualScoringProblems(studentWorksheetIdList)

    return data
  }
}

export const lessonWorksheetController = new LessonWorksheetController()
